import { createQueryMultipleDestinationIds } from '~/helpers/search/createQueryMultipleDestinationIds'
import { constructReplica } from '~/helpers/search/constructReplica'
import { createDates } from '~/helpers/search/createDates'
import { createFacetFilters } from '~/helpers/search/createFacetFilters'
import { createNumericFilters } from '~/helpers/search/createNumericFilters'
import { createQueryMultipleHouseIds } from '~/helpers/search/createQueryMultipleHouseIds'
import { findReplica } from '~/helpers/search/findReplica'
import { getBudget } from '~/helpers/search/getBudget'
import { handlePage } from '~/helpers/search/handlePage'
import { getDestinations } from '~/helpers/search/getDestinations'
import { isValidLat, isValidLng } from '@/helpers/search/geo'

import type { LocationQuery } from 'vue-router'
import type {
  InputSearchQuery,
  StateSearchBudgetFilters,
  StateSearchNumericFilter,
  StateSearchFacetFilter,
  StateSearchDateFilters,
  StateSearchLocationFilters,
  StateSearchReplicaFilters,
  StateSearchResults,
} from '~/types/search/types'

export const useSearchInitStates = ({
  typeSearch,
}: {
  typeSearch: 'public' | 'admin' | 'b2b2c'
}) => {
  const config = useRuntimeConfig()
  const { $api, $httpCookies, $lcRepositories } = useNuxtApp()
  const { locale, t } = useI18n()
  const { isMobile } = useDevice()
  const route = useRoute()
  const { currency } = useUser()
  const { data, setData } = useSearchInput()
  const { setBounds, resetBounds } = useSearchSplitMap()

  const stateSearchNumericFilter = useState<StateSearchNumericFilter>(
    'state-search-numeric-filter',
  )
  const stateSearchFacetFilter = useState<StateSearchFacetFilter>(
    'state-search-facet-filter',
  )
  const stateSearchDateFilters = useState<StateSearchDateFilters>(
    'state-search-date-filter',
  )
  const stateSearchBudgetFilters = useState<StateSearchBudgetFilters>(
    'state-search-budget-filter',
  )
  const stateSearchLocationFilters = useState<StateSearchLocationFilters>(
    'state-search-location-filter',
  )
  const stateSearchReplicaFilters = useState<StateSearchReplicaFilters>(
    'state-search-replica-filter',
  )
  const stateSearchResults = useState<StateSearchResults>(
    'state-search-results',
  )

  const fillStates = ({
    destinations,
    destination,
    queryOutput,
  }: {
    destinations?: InputSearchQuery[]
    destination?: { id: string; name: string; coords?: number[] }
    queryOutput: LocationQuery
  }) => {
    // Get replica
    const replicas = constructReplica({ config, userType: typeSearch })
    const replica = findReplica(replicas, route.query)

    let query: Partial<InputSearchQuery> = {}
    // NumericFilters
    const { selectedNumericFilters } = createNumericFilters(route.query)

    // Date
    const { endDate, startDate } = createDates({
      query: route.query,
      $httpCookies,
    })

    // Price
    const { minBudget, maxBudget } = getBudget({
      query: route.query,
      userCurrency: currency.value,
    })

    // FacetFilters
    const { facetFilters } = createFacetFilters(queryOutput, locale)

    // Page
    const { page, showPreviousButton } = handlePage(route.query, isMobile)

    if (destination && !isNaN(Number(destination.id))) {
      query = {
        label: destination.name,
        value: Number(destination.id),
      }

      setData('location', {
        id: destination.id,
        name: destination.name,
        type: 'destination',
      })
    } else if (typeof destination?.id === 'string') {
      query = {
        coords: destination.coords,
        label: destination.name,
      }

      setData('location', {
        coords: destination.coords,
        id: destination.id,
        name: destination.name,
        type: 'destination',
      })
    } else setData('location', {})

    const [neLat, neLng, swLat, swLng] = ['neLat', 'neLng', 'swLat', 'swLng']
      .map((param) => route.query[param]?.toString())
      .map(Number)

    const hasAllCoords = [neLat, neLng, swLat, swLng].every((n) => !isNaN(n))

    const coordsAreValid =
      isValidLat(neLat) &&
      isValidLng(neLng) &&
      isValidLat(swLat) &&
      isValidLng(swLng)

    if (hasAllCoords && coordsAreValid) {
      setBounds([neLat, neLng, swLat, swLng])
      const destinationName =
        data.value.location.name ?? route.query.fullName?.toString()
      setData('location', {
        id: 'area'.concat(destinationName ? '_unknown' : ''),
        name: destinationName ?? t('search.geographicArea'),
        coords: getCenterPointFromBoundingBox(neLat, neLng, swLat, swLng),
      })
    } else {
      resetBounds()
    }

    const parsedStartDate = $dayjs(startDate.value, { format: 'YYYY-MM-DD' })
    const parsedEndDate = $dayjs(endDate.value, { format: 'YYYY-MM-DD' })

    setData('guest', selectedNumericFilters.capacity)
    setData(
      'startDate',
      parsedStartDate.isValid() ? parsedStartDate.toDate() : null,
    )
    setData('endDate', parsedEndDate.isValid() ? parsedEndDate.toDate() : null)

    // Multiple houses ids
    const queryMultipleHousesIds = createQueryMultipleHouseIds(route.query)

    // Multiple destinations ids
    const queryMultipleDestinationsIds = createQueryMultipleDestinationIds(
      route.query,
    )

    let queryMultiple: InputSearchQuery[] = []
    if (destinations) {
      queryMultiple = destinations
    }

    stateSearchNumericFilter.value = {
      selectedNumericFilters: {
        bathrooms: selectedNumericFilters.bathrooms || 0,
        bedrooms: selectedNumericFilters.bedrooms || 0,
        capacity: selectedNumericFilters.capacity || 0,
      },
    }

    stateSearchFacetFilter.value = {
      facetFilters,
    }

    stateSearchDateFilters.value = {
      endDate: startDate.value && endDate.value ? endDate.value : null,
      startDate: startDate.value && endDate.value ? startDate.value : null,
    }

    stateSearchBudgetFilters.value = {
      maxBudget: maxBudget.value,
      minBudget: minBudget.value,
    }

    stateSearchLocationFilters.value = {
      query,
      queryMultiple,
      queryMultipleHousesIds,
      queryMultipleDestinationsIds,
    }

    stateSearchReplicaFilters.value = {
      replicas: replicas.value,
      currentReplica: replica.value,
    }

    stateSearchResults.value.page = page.value
    stateSearchResults.value.meta.showPreviousButton = showPreviousButton.value
  }

  const fetchAndFillStates = async (destinationParams?: {
    id: string
    name: string
    queryOutput: LocationQuery
  }) => {
    let destination:
      | { id: string; name: string; coords?: number[] }
      | undefined = undefined

    if (destinationParams?.id && destinationParams?.name) {
      destination = {
        id: destinationParams.id,
        name: destinationParams.name,
      }
    }

    if (
      route.query.destinationId &&
      data.value.location.name &&
      route.query.destinationId.toString() ===
        data.value.location?.id?.toString()
    ) {
      destination = {
        id: data.value.location.id,
        name: data.value.location.name,
      }
    } else if (
      route.query.destinationId &&
      route.query.destinationId.toString() !==
        data.value.location?.id?.toString()
    ) {
      const { data } = await $lcRepositories.houses.getDestinationById({
        destinationId: route.query.destinationId.toString(),
      })

      if (data?.name)
        destination = {
          id: route.query.destinationId.toString(),
          name: data.name,
        }
    } else if (
      data.value.location.id?.toString().startsWith('area') &&
      data.value.location.name &&
      Array.isArray(data.value.location.coords) &&
      data.value.location.coords.length === 2
    ) {
      destination = {
        id: data.value.location.id,
        name: data.value.location.name,
        coords: data.value.location.coords,
      }
    }

    fillStates({
      queryOutput: destinationParams?.queryOutput || route.query,
      destination,
    })
  }

  const fetchAndFillStatesMultiple = async (destinationParams?: {
    id: string
    name: string
  }) => {
    let destinations: { name: string; id: number }[] = []
    const arrayDestiIds =
      route.query.multipleDestinationsIds?.toString().split(',') || []
    const arrayHouseIds =
      route.query.multipleHousesIds?.toString().split(',') || []

    const allIds = [
      ...arrayDestiIds,
      ...(!arrayDestiIds.length &&
      !arrayHouseIds.length &&
      destinationParams?.id
        ? [destinationParams.id]
        : []),
    ]

    if (allIds.length) {
      destinations = (await getDestinations({
        $api,
        destinationId: allIds,
        locale: locale.value,
      })) as { name: string; id: number }[]
    }

    const locationDestinations = destinations.map<InputSearchQuery>((d) => ({
      value: d.id,
      label: d.name,
      type: 'location' as const,
    }))

    fillStates({
      queryOutput: route.query,
      destinations: locationDestinations,
      destination:
        !arrayDestiIds.length && !arrayHouseIds.length
          ? destinationParams
          : undefined,
    })
  }

  return {
    fetchAndFillStatesMultiple,
    fetchAndFillStates,
  }
}
